import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Modal from '../common/Modal';
import Badge from '../common/Badge';
import { companyApi } from '../../api/companyApi';
import { formatDate } from '../../utils/formatDate';
import { Building2, Users, Hash, UserCheck, Shield, Mail, Phone, Calendar, Edit, ExternalLink } from 'lucide-react';

const CompanyDetailsModal = ({ companyId, isOpen, onClose, onEdit }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDetails = async () => {
      if (!companyId) return;
      setLoading(true);
      try {
        const res = await companyApi.getCompanyById(companyId);
        setData(res.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    if (isOpen) {
      fetchDetails();
    }
  }, [companyId, isOpen]);

  if (!isOpen) return null;

  const { company, employees = [], idConfig } = data || {};

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Company Tenant Details" maxWidth="max-w-3xl">
      {loading || !company ? (
        <div className="py-12 text-center text-xs text-slate-500">Loading company profile...</div>
      ) : (
        <div className="space-y-6">
          {/* Header summary */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4 rounded-2xl bg-white/80 border border-slate-100">
            <div className="flex items-center space-x-4">
              <img
                src={company.logo || 'https://images.unsplash.com/photo-1560179707-f14e90ef3623?w=150&auto=format&fit=crop&q=80'}
                alt={company.name}
                className="w-16 h-16 rounded-2xl object-cover border border-slate-700"
              />
              <div>
                <div className="flex items-center space-x-2">
                  <h3 className="text-lg font-bold text-slate-900 font-display">{company.name}</h3>
                  <Badge variant={company.status}>{company.status}</Badge>
                </div>
                <p className="text-xs text-slate-500 mt-0.5">{company.industry}</p>
                <div className="flex items-center space-x-4 mt-2 text-xs text-slate-600">
                  <span className="flex items-center space-x-1">
                    <Calendar className="w-3.5 h-3.5 text-green-700" />
                    <span>Onboarded: {formatDate(company.onboardedAt)}</span>
                  </span>
                  <span className="flex items-center space-x-1">
                    <Hash className="w-3.5 h-3.5 text-green-700" />
                    <span>Prefix: <strong className="text-slate-900">{company.employeeIdPrefix}</strong></span>
                  </span>
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              {onEdit && (
                <button
                  onClick={() => {
                    onClose();
                    onEdit(company);
                  }}
                  className="px-3 py-1.5 rounded-xl bg-green-700 hover:bg-green-600 text-white font-semibold text-xs flex items-center space-x-1.5 transition"
                >
                  <Edit className="w-3.5 h-3.5" />
                  <span>Edit Limit & Profile</span>
                </button>
              )}
              <button
                onClick={() => {
                  onClose();
                  navigate(`/companies/${company._id}`);
                }}
                className="px-3 py-1.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-600 hover:text-slate-900 text-xs flex items-center space-x-1.5 transition"
              >
                <ExternalLink className="w-3.5 h-3.5 text-green-700" />
                <span>Full Page</span>
              </button>
            </div>
          </div>

          {/* Stats Bar */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="p-4 rounded-2xl bg-white/50 border border-slate-100">
              <p className="text-xs text-slate-500">Employee Lock Limit</p>
              <p className="text-xl font-bold text-slate-900 font-display mt-1">{company.employeeLimit} Max</p>
            </div>
            <div className="p-4 rounded-2xl bg-white/50 border border-slate-100">
              <p className="text-xs text-slate-500">Current Employees</p>
              <p className="text-xl font-bold text-emerald-600 font-display mt-1">{company.currentEmployeeCount}</p>
            </div>
            <div className="p-4 rounded-2xl bg-white/50 border border-slate-100">
              <p className="text-xs text-slate-500">Next Employee ID</p>
              <p className="text-xl font-bold text-green-700 font-display mt-1">
                {idConfig
                  ? `${idConfig.prefix}${String(idConfig.currentSequence + 1).padStart(4, '0')}`
                  : `${company.employeeIdPrefix}0001`}
              </p>
            </div>
          </div>

          {/* Enabled Attendance Methods */}
          <div className="p-3.5 rounded-2xl bg-white/40 border border-slate-100/80">
            <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Enabled Attendance Methods</p>
            <div className="flex flex-wrap gap-2">
              <span className={`px-2.5 py-1 rounded-lg text-xs font-medium border ${company.enableFaceAttendance !== false ? 'bg-green-600 border-transparent text-white' : 'bg-white border-slate-100 text-slate-500 line-through'}`}>
                📸 Face Recognition {company.enableFaceAttendance !== false ? '✓' : '✗'}
              </span>
              <span className={`px-2.5 py-1 rounded-lg text-xs font-medium border ${company.enableQrScan !== false ? 'bg-green-600 border-transparent text-white' : 'bg-white border-slate-100 text-slate-500 line-through'}`}>
                📱 QR Code Scanner {company.enableQrScan !== false ? '✓' : '✗'}
              </span>
              <span className={`px-2.5 py-1 rounded-lg text-xs font-medium border ${company.enableInstantAttendance !== false ? 'bg-green-600 border-transparent text-white' : 'bg-white border-slate-100 text-slate-500 line-through'}`}>
                ⚡ Instant Logging {company.enableInstantAttendance !== false ? '✓' : '✗'}
              </span>
            </div>
          </div>

          {/* Admin Details */}
          {company.adminId && (
            <div className="p-4 rounded-2xl bg-white/40 border border-slate-100/80">
              <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3 flex items-center space-x-1.5">
                <Shield className="w-4 h-4 text-green-700" />
                <span>Primary Admin Account</span>
              </h4>
              <div className="flex items-center justify-between text-xs text-slate-600">
                <div>
                  <p className="font-semibold text-slate-900">{company.adminId.name}</p>
                  <p className="text-slate-500 flex items-center space-x-1 mt-0.5">
                    <Mail className="w-3 h-3 text-slate-500" />
                    <span>{company.adminId.email}</span>
                  </p>
                </div>
                <div className="text-right">
                  <Badge variant={company.adminId.status}>{company.adminId.status}</Badge>
                  {company.adminId.phone && (
                    <p className="text-slate-500 flex items-center justify-end space-x-1 mt-1">
                      <Phone className="w-3 h-3 text-slate-500" />
                      <span>{company.adminId.phone}</span>
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Recent Employees Table */}
          <div>
            <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3 flex items-center space-x-1.5">
              <Users className="w-4 h-4 text-green-700" />
              <span>Employees List ({employees.length})</span>
            </h4>
            {employees.length === 0 ? (
              <p className="text-xs text-slate-500 italic">No employees added yet.</p>
            ) : (
              <div className="overflow-x-auto rounded-xl border border-slate-100">
                <table className="w-full text-left text-xs">
                  <thead className="bg-white text-slate-500 uppercase">
                    <tr>
                      <th className="py-2.5 px-4">Employee ID</th>
                      <th className="py-2.5 px-4">Name</th>
                      <th className="py-2.5 px-4">Department</th>
                      <th className="py-2.5 px-4">Joined Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 text-slate-600">
                    {employees.map((emp) => (
                      <tr key={emp._id} className="hover:bg-slate-50/40">
                        <td className="py-2.5 px-4 font-mono text-green-700 font-semibold">{emp.employeeId}</td>
                        <td className="py-2.5 px-4 font-medium text-slate-900">{emp.name}</td>
                        <td className="py-2.5 px-4">{emp.department}</td>
                        <td className="py-2.5 px-4 text-slate-500">{formatDate(emp.joinedDate)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
};

export default CompanyDetailsModal;
